import { useQuery } from '@tanstack/react-query';
import { getOrganizationSettings } from '@core/api/getOrganizationSettings';
import {
  AppVersion,
  DEFAULT_ORGANIZATION_APP_VERSION,
  getOrganizationAppVersion,
} from '@core/constants/appVersion';
import { ORGANIZATION_SETTINGS_QUERY_KEY } from '@core/hooks/useOrganizationSettings';
import useStudyInfo from '@core/hooks/useStudyInfo';
import { OrganizationSettings } from '@core/types/OrganizationSettings';

// Resolves which app version a shared-study link (/study/view?s=<hash>) should
// be served on: the organization-wide viewer version of the study's owner org.
//
// Settings are fetched by the *study's* organization id rather than through
// useOrganizationSettings, which prefers the signed-in user's current org —
// a logged-in user opening another org's link would otherwise read the wrong
// setting.
//
// Any failure resolves to the v2 default, matching the behaviour before the
// setting existed.
const useSharedStudyAppVersion = (
  studyHash: string,
): { isResolved: boolean; targetVersion: AppVersion } => {
  const { studyInfo, isError: isStudyError } = useStudyInfo({
    studyInstanceUidOrHash: studyHash,
    isSharedStudy: true,
    useErrorBoundary: false,
  });
  const organizationId = studyInfo?.organizationId;

  const {
    data: organizationSettings,
    isSuccess,
    isError,
  } = useQuery<OrganizationSettings>({
    queryKey: [ORGANIZATION_SETTINGS_QUERY_KEY, organizationId],
    queryFn: () => getOrganizationSettings(organizationId),
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 60 * 12,
    cacheTime: 1000 * 60 * 60 * 12.5,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const isResolved = !studyHash || isStudyError || isSuccess || isError;
  const targetVersion = isSuccess
    ? getOrganizationAppVersion(organizationSettings)
    : DEFAULT_ORGANIZATION_APP_VERSION;

  return { isResolved, targetVersion };
};

export default useSharedStudyAppVersion;
